import { Subscription } from "rxjs";
import { TrafficFeature } from "./webgl-maplibre.types";
import { emptyFC } from "./webgl-maplibre.helpers";
import { MapLayerAsGeoJsonGQL, MapLayerGeoJson, MapLayerUpdatesGQL } from "../efb-api/efb-api.service";
import { environment } from "../../../environments/environment";

export interface TrafficDependencies {
  authService: { jwtIsReadySync: boolean };
  mapLayerAsGeoJsonGQL: MapLayerAsGeoJsonGQL;
  mapLayerUpdatesGQL: MapLayerUpdatesGQL;
  onTrafficUpdate: (data: any) => void;
  onFeaturesRendered: (fc: GeoJSON.FeatureCollection) => void;
}

export class TrafficManager {
  private subscription: Subscription | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private trafficId: string | null = null;

  constructor(private deps: TrafficDependencies) {}

  start(): void {
    if (this.interval) return;
    if (this.deps.authService.jwtIsReadySync) {
      this.initSubscription();
    }

    // The interval only opens the subscription again. It sends no position:
    // no server code has read the EFB position since efb-mission-go v0.5.0,
    // and each request failed after 2 s.
    this.interval = setInterval(() => {
      if (!this.deps.authService.jwtIsReadySync) return;
      if (!this.subscription || this.subscription.closed) {
        this.initSubscription();
      }
    }, 10000);
  }

  stop(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.trafficId = null;
  }

  private initSubscription(): void {
    const me = this;
    this.subscription = this.deps.mapLayerUpdatesGQL
      .subscribe({
        id: environment.droneSubscriptionId,
      })
      .subscribe({
        next(result) {
          if (result.data.mapLayerUpdates === null) return;
          const mapLayerUpdate = result.data.mapLayerUpdates;
          me.trafficId = mapLayerUpdate.id;
          me.deps.mapLayerAsGeoJsonGQL.mutate({ id: environment.droneSubscriptionId }).subscribe({
            next(res) {
              if (res === null || res.data.mapLayerAsGeoJson === null) return;
              const mapLayerAsGeoJson = res.data.mapLayerAsGeoJson as MapLayerGeoJson;
              mapLayerAsGeoJson.features.features = mapLayerAsGeoJson.features.features.filter(
                (f: any) => f.properties.status === "OK"
              );
              me.deps.onTrafficUpdate(mapLayerAsGeoJson);
              me.deps.onFeaturesRendered(TrafficManager.buildTrafficFC(mapLayerAsGeoJson.features.features as any));
            },
            error(err) {
              console.log("Error receiving traffic: ", err);
            },
          });
        },
        error(err) {
          console.log("Error receive traffic: ", err);
        },
      });
  }

  static buildTrafficFC(features: TrafficFeature[]): GeoJSON.FeatureCollection {
    if (!features || features.length === 0) return emptyFC();
    const geoFeatures: GeoJSON.Feature[] = features.map((el) => {
      const radius = el.properties.flightType === "normal" ? 12 : 16;
      const color = el.properties.flightType === "normal" ? "#ffcc33" : "#4108dd";
      return {
        type: "Feature" as const,
        properties: {
          description: el.properties.description,
          color,
          radius,
        },
        geometry: {
          type: "Point" as const,
          coordinates: [el.properties.lon, el.properties.lat],
        },
      };
    });
    return { type: "FeatureCollection", features: geoFeatures };
  }
}
